import React, { useMemo } from 'react'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useLogUserEvent } from '../../../hooks/useLogUserEvent'
import useFetchEntitiesWithCache from '../../../hooks/useFetchEntitiesWithCache'
import { issueFiltersAtom } from '../../../atoms/issueAtom'
import { useFilterIssues } from '../../issues/utils'
import { IssueMarket } from './IssueMarketOverview'
import Box from '@mui/material/Box'
import RoutedDropdownSelect from '../../components/RoutedDropdownSelect'
import { useQuery } from '../../../hooks/useQuery'
import { Navigate } from 'react-router'
import { OngoingIssuesStats } from './IssueMarketOngoingIssuesStats'
import { endOfQuarter, isBefore, subQuarters } from 'date-fns'
import { getIssueDateToFilter } from '../../../components/utils/categorizeIssues'
import { USER_EVENT_TYPES } from '@finance-lab/utils/types'

const dropdownItemKeys = [
    { id: 'overview', labelKey: 'pages.statistics.issueMarket.overview' },
    { id: 'ongoing-issues-stats', labelKey: 'pages.statistics.issueMarket.ongoingIssues' },
] as const

export const IssueMarketView = () => {
    useLogUserEvent(USER_EVENT_TYPES.SHOW_STATS_ISSUE_MARKET)
    const { t } = useTranslation()
    const { isLoading, result: fetchedIssues } = useFetchEntitiesWithCache('issue')
    const [filters] = useAtom(issueFiltersAtom)
    const filteredIssues = useFilterIssues(fetchedIssues || [], filters)
    const query = useQuery()
    const graphRoutingParam = 'graph'
    const defaultGraph = 'overview'
    const selectedGraph: (typeof dropdownItemKeys)[number]['id'] = query.get(graphRoutingParam) || defaultGraph

    const dropdownItems = useMemo(() => dropdownItemKeys.map(({ id, labelKey }) => ({ id, label: t(labelKey) })), [t])

    const today = new Date()
    const endOfLastQuarter = endOfQuarter(subQuarters(today, 1))

    const graphSelector = (
        <Box sx={{ marginBottom: 4 }}>
            <RoutedDropdownSelect
                defaultItem={defaultGraph}
                items={dropdownItems}
                label={t('pages.statistics.issueMarket.selectChart')}
                routingParam={graphRoutingParam}
            />
        </Box>
    )

    const issuesExludingCurrentQuarter = filteredIssues.filter((issue) =>
        isBefore(getIssueDateToFilter(issue), endOfLastQuarter),
    )

    if (selectedGraph === 'overview') {
        return <IssueMarket graphSelector={graphSelector} isLoading={isLoading} issues={issuesExludingCurrentQuarter} />
    }

    if (selectedGraph === 'ongoing-issues-stats') {
        return (
            <OngoingIssuesStats
                graphSelector={graphSelector}
                hasTable
                isLoading={isLoading}
                issues={filteredIssues}
                metricType='ongoing-issues'
            />
        )
    }

    return <Navigate to='?graph=overview' replace />
}
